import React, { useState, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Mail,
  MessageSquare,
  Clock,
  Send,
  Building2,
  CheckCircle2,
  User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import {
  staggerContainer,
  fadeInUp,
  floatingAnimation,
  floatingTransition,
  floatingAnimationAlt,
  floatingTransitionAlt,
  viewportOptions,
} from "@/lib/animations";

// ============================================================================
// VALIDATION HELPERS
// ============================================================================
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateField = (fieldName, value) => {
  const trimmedValue = value?.trim() || "";

  switch (fieldName) {
    case "name":
      if (!trimmedValue) {
        return "Name is required";
      }
      if (trimmedValue.length < 2) {
        return "Name must be at least 2 characters";
      }
      if (!/^[a-zA-Z\s'-]+$/.test(trimmedValue)) {
        return "Name can only contain letters, spaces, hyphens, and apostrophes";
      }
      return null;

    case "email":
      if (!trimmedValue) {
        return "Email address is required";
      }
      if (!EMAIL_REGEX.test(trimmedValue)) {
        return "Please enter a valid email address";
      }
      return null;

    case "message":
      if (!trimmedValue) {
        return "Message is required";
      }
      if (trimmedValue.length < 10) {
        return "Message must be at least 10 characters";
      }
      return null;

    default:
      return null;
  }
};

// ============================================================================
// HERO SECTION
// ============================================================================
const HeroSection = () => {
  return (
    <section className="relative py-20 overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-amber-50/30 to-slate-100" />

      {/* Geometric Pattern Overlay */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* Floating Elements */}
      <motion.div
        className="absolute top-10 right-20 w-48 h-48 rounded-full bg-gradient-to-br from-amber-200/20 to-orange-200/20 blur-3xl"
        animate={floatingAnimation}
        transition={floatingTransition}
      />
      <motion.div
        className="absolute bottom-10 left-10 w-64 h-64 rounded-full bg-gradient-to-br from-slate-200/30 to-blue-200/20 blur-3xl"
        animate={floatingAnimationAlt}
        transition={floatingTransitionAlt}
      />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="max-w-3xl mx-auto text-center"
        >
          {/* Badge */}
          <motion.div variants={fadeInUp} className="mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/5 border border-slate-200 text-slate-700 text-sm font-medium">
              <MessageSquare className="w-4 h-4 text-amber-600" />
              We're Here to Help
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            variants={fadeInUp}
            className="font-display text-4xl md:text-5xl font-bold text-slate-900 leading-[1.1] tracking-tight mb-6"
          >
            Get in{" "}
            <span className="relative">
              <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-amber-700 via-amber-600 to-orange-600">
                Touch
              </span>
              <motion.span
                className="absolute bottom-1 left-0 right-0 h-3 bg-amber-200/50 -z-0"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.8, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                style={{ originX: 0 }}
              />
            </span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            variants={fadeInUp}
            className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed"
          >
            Questions, feedback, or just want to say hello?
            Our team is ready to hear from you.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// CONTACT FORM INPUT
// ============================================================================
const FormInput = ({
  label,
  name,
  type = "text",
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  touched,
  maxLength,
  icon: Icon,
  textarea = false,
}) => {
  const showError = touched && error;
  const inputId = name;

  const baseClasses = `
    w-full px-4 py-3 pl-12
    bg-white border rounded-xl
    text-slate-900 placeholder-slate-400
    transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-offset-0
    ${showError
      ? "border-red-300 focus:border-red-400 focus:ring-red-100"
      : "border-slate-200 focus:border-amber-400 focus:ring-amber-100"
    }
  `;

  return (
    <div className="mb-5">
      <label
        htmlFor={inputId}
        className="block mb-2 text-sm font-medium text-slate-700"
      >
        {label}
        <span className="text-red-400 ml-1">*</span>
      </label>
      <div className="relative">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
          <Icon className={`w-5 h-5 ${showError ? "text-red-400" : "text-slate-400"}`} />
        </div>
        {textarea ? (
          <textarea
            id={inputId}
            name={name}
            rows={4}
            className={`${baseClasses} pl-12 min-h-[120px] resize-none`}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            maxLength={maxLength}
            aria-invalid={showError ? "true" : "false"}
            aria-describedby={showError ? `${inputId}-error` : undefined}
          />
        ) : (
          <input
            id={inputId}
            name={name}
            type={type}
            className={baseClasses}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            maxLength={maxLength}
            aria-invalid={showError ? "true" : "false"}
            aria-describedby={showError ? `${inputId}-error` : undefined}
          />
        )}
      </div>
      <div className="flex justify-between mt-1.5">
        {showError ? (
          <p
            id={`${inputId}-error`}
            className="text-red-500 text-sm flex items-center gap-1"
            role="alert"
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {error}
          </p>
        ) : (
          <span />
        )}
        {maxLength && (
          <span className="text-xs text-slate-400">
            {value.length}/{maxLength}
          </span>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// CONTACT FORM SECTION
// ============================================================================
const ContactFormSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [touched, setTouched] = useState({
    name: false,
    email: false,
    message: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Compute validation errors
  const errors = useMemo(() => ({
    name: validateField("name", formData.name),
    email: validateField("email", formData.email),
    message: validateField("message", formData.message),
  }), [formData]);

  // Check if form is valid
  const isFormValid = useMemo(
    () => !errors.name && !errors.email && !errors.message,
    [errors]
  );

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Mark all fields as touched
    setTouched({ name: true, email: true, message: true });

    // Check validation
    if (!isFormValid) {
      toast.error("Please fix the errors before submitting");
      return;
    }

    setIsSubmitting(true);

    // Sanitize inputs
    const sanitizedName = formData.name.replace(/[^a-zA-Z-' ]/g, "");
    const sanitizedMessage = formData.message
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

    try {
      const response = await fetch("/api/sendEmail", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: sanitizedName,
          email: formData.email,
          message: sanitizedMessage,
        }),
      });

      if (response.ok) {
        setFormData({ name: "", email: "", message: "" });
        setTouched({ name: false, email: false, message: false });
        toast.success("Message sent successfully! We'll get back to you soon.");
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.error || "Failed to send message. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email Us",
      value: "support@propertybulk.com",
      description: "We'll respond within 24 hours",
    },
    {
      icon: Clock,
      title: "Response Time",
      value: "< 24 Hours",
      description: "For most inquiries",
    },
    {
      icon: MessageSquare,
      title: "Live Support",
      value: "Business Hours",
      description: "Mon-Fri, 9am-5pm EST",
    },
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Form */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewportOptions.immediate}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <span className="inline-block px-4 py-1.5 rounded-full bg-amber-100 text-amber-800 text-sm font-medium mb-4">
                Send a Message
              </span>
              <h2 className="font-display text-3xl font-bold text-slate-900 mb-2">
                We'd Love to Hear From You
              </h2>
              <p className="text-slate-600 mb-8">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>
            </motion.div>

            <motion.form
              variants={fadeInUp}
              onSubmit={handleSubmit}
              className="space-y-1"
            >
              <FormInput
                label="Your Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter your name"
                error={errors.name}
                touched={touched.name}
                maxLength={50}
                icon={User}
              />

              <FormInput
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter your email"
                error={errors.email}
                touched={touched.email}
                maxLength={100}
                icon={Mail}
              />

              <FormInput
                label="Message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="How can we help you?"
                error={errors.message}
                touched={touched.message}
                maxLength={1000}
                icon={MessageSquare}
                textarea
              />

              {/* Form status messages */}
              {isFormValid && formData.name && formData.email && formData.message && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-emerald-600 text-sm flex items-center gap-1.5 mb-4"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Ready to send!
                </motion.p>
              )}

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 text-lg font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-xl shadow-lg shadow-slate-900/20 hover:shadow-xl hover:shadow-slate-900/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Send Message
                    <Send className="w-5 h-5" />
                  </span>
                )}
              </Button>
            </motion.form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewportOptions.standard}
            variants={staggerContainer}
            className="lg:pl-8"
          >
            <motion.div variants={fadeInUp} className="mb-8">
              <span className="inline-block px-4 py-1.5 rounded-full bg-slate-100 text-slate-600 text-sm font-medium mb-4">
                Contact Information
              </span>
              <h2 className="font-display text-3xl font-bold text-slate-900 mb-2">
                Other Ways to Reach Us
              </h2>
              <p className="text-slate-600">
                Choose the method that works best for you.
              </p>
            </motion.div>

            <div className="space-y-6">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="flex items-start gap-4 p-6 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-100"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center">
                    <item.icon className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                    <p className="text-lg font-medium text-slate-700">{item.value}</p>
                    <p className="text-sm text-slate-500">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* FAQ Callout */}
            <motion.div
              variants={fadeInUp}
              className="mt-8 p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200"
            >
              <h3 className="font-display text-lg font-semibold text-slate-900 mb-2">
                Looking for quick answers?
              </h3>
              <p className="text-slate-600 mb-4">
                Many common questions are answered in our documentation and FAQ section.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-amber-700 font-medium hover:text-amber-800 transition-colors"
              >
                Learn more about us
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// FOOTER
// ============================================================================
const Footer = () => {
  return (
    <footer className="py-12 bg-slate-950 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-display text-xl font-bold text-white">
              PropertyBulk
            </span>
          </div>

          <div className="flex items-center gap-8 text-sm text-slate-400">
            <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
            <Link to="/about" className="hover:text-white transition-colors">About</Link>
          </div>

          <div className="text-sm text-slate-500">
            © 2024 PropertyBulk. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============================================================================
// MAIN CONTACT COMPONENT
// ============================================================================
const Contact = () => {
  return (
    <div className="min-h-screen bg-white">
      <HeroSection />
      <ContactFormSection />
      <Footer />
    </div>
  );
};

export default Contact;
